import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { IMeasurement } from "../../../../@types/admin/AdminMeasurement";
import {
  IAdminPatientDetail,
  IPatientCondition,
  IPatientMedication,
} from "../../../../@types/admin/AdminPatient";
import { TableSorted } from "../../../../components/TableSorted";
import {
  PATIENT_CONDITIONS_QUERY_KEY,
  usePatientConditions,
  usePatientMeasurements,
  usePatientMedications,
} from "../../../../hooks/queries/admin/patients";
import { useModal } from "../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import {
  ErrorMessage,
  FilterButton,
  HeaderActions,
  SectionCard,
  SectionCaption,
  SectionHeader,
  SectionHeading,
  SectionTitle,
  SectionTitleGroup,
} from "../AdminPage.styles";
import { PageInfoTooltip } from "../PageInfoTooltip";
import { PatientBaselinesPanel } from "../PatientBaselinesPanel";
import { TableContainer } from "../TableContainer";
import { TablePagination } from "../TablePagination";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";
import { useMockPagination } from "../useMockPagination";
import { ChangeConditionModal } from "./ChangeConditionModal";
import { MeasurementDetailModal } from "./MeasurementDetailModal";
import { PanelStack } from "./PatientDetailsPanel.styles";

const MEASUREMENTS_PAGE_SIZE = 10;

interface IPatientClinicalTabProps {
  patient: IAdminPatientDetail;
}

/**
 * Clinical tab of the patient detail panel (EN-5169): conditions,
 * measurements, baselines and medications.
 *
 * Retool had these as four containers stacked on the one PatientDetails
 * screen, below the two forms and above six more. Grouped here because they
 * answer the same question — what has been recorded about this patient — and
 * because thirteen tables in one scroll is what the tabs exist to avoid.
 *
 * Baselines is the shared `PatientBaselinesPanel` (EN-5168), not a re-port of
 * `page_Patients/lib/getBaselines.sql`. The Retool page had its own copy of
 * that query, with the `~ '^[0-9]+$'` guard the Baselines page's copy is still
 * missing — a fix that never made it back.
 */
export function PatientClinicalTab({ patient }: IPatientClinicalTabProps) {
  const patientId = Number(patient.id);
  const { openModal, closeModal } = useModal();

  const {
    conditions,
    isLoading: conditionsLoading,
    isError: conditionsError,
  } = usePatientConditions(patientId);
  const {
    medications,
    isLoading: medicationsLoading,
    isError: medicationsError,
  } = usePatientMedications(patientId);

  const [measurementsPage, setMeasurementsPage] = useState(1);
  const [measurementsPageSize, setMeasurementsPageSize] = useState(
    MEASUREMENTS_PAGE_SIZE
  );
  const {
    measurements,
    totalCount: measurementsTotal,
    isFetching: measurementsFetching,
    isError: measurementsError,
  } = usePatientMeasurements(patientId, measurementsPage, measurementsPageSize);

  const medicationsPagination = useMockPagination({
    data: medications,
    searchFields: ["brandName", "regime", "route", "status"],
  });

  /** The condition the patient is currently on — the published one. */
  const currentCondition = conditions.find((condition) => condition.publish);

  const conditionColumns: ColumnDef<IPatientCondition, string>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Condition",
        accessorFn: (condition) => condition.name ?? "-",
        enableSorting: true,
      },
      {
        // Retool showed publish as a raw boolean column. "Current" is what the
        // flag means on this table: the published condition is the one in use.
        id: "publish",
        header: "State",
        accessorFn: (condition) =>
          condition.publish ? "Current" : "Superseded",
        enableSorting: true,
      },
      { accessorKey: "slug", header: "Slug", enableSorting: true },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "modified",
        header: "Modified",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.modified, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        // Not in Retool's table, which read cms_condition directly and did not
        // select these. The endpoint returns them and they are the clinically
        // interesting part of a condition record.
        accessorKey: "diagnosisDate",
        header: "Diagnosed",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.diagnosisDate, "-")}</span>
        ),
        enableSorting: true,
      },
    ],
    []
  );

  const measurementColumns: ColumnDef<IMeasurement, string>[] = useMemo(
    () => [
      {
        id: "id",
        header: "ID",
        accessorFn: (measurement) => String(measurement.id),
        enableSorting: true,
      },
      {
        accessorKey: "time",
        header: "Time",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.time, "-")}</span>
        ),
        enableSorting: true,
      },
      { accessorKey: "source", header: "Source", enableSorting: true },
      {
        id: "measurementTypes",
        header: "Recorded",
        accessorFn: (measurement) =>
          (measurement.measurementTypes ?? []).join(", "),
        enableSorting: true,
      },
      {
        id: "isArchived",
        header: "State",
        accessorFn: (measurement) =>
          measurement.isArchived ? "Archived" : "Active",
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <FilterButton
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              openModal(<MeasurementDetailModal measurement={row.original} />, {
                width: "min(760px, 94vw)",
                maxHeight: "90vh",
                showCloseButton: true,
              });
            }}
          >
            View values
          </FilterButton>
        ),
      },
    ],
    []
  );

  const medicationColumns: ColumnDef<IPatientMedication, string>[] = useMemo(
    () => [
      { accessorKey: "brandName", header: "Medication", enableSorting: true },
      {
        accessorKey: "dosageAndUnit",
        header: "Dosage",
        enableSorting: true,
      },
      { accessorKey: "regime", header: "Regime", enableSorting: true },
      { accessorKey: "route", header: "Route", enableSorting: true },
      {
        accessorKey: "startDate",
        header: "Start",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.startDate, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "endDate",
        header: "End",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.endDate, "Ongoing")}</span>
        ),
        enableSorting: true,
      },
      { accessorKey: "status", header: "Status", enableSorting: true },
      { accessorKey: "source", header: "Source", enableSorting: true },
      {
        accessorKey: "codeSystem",
        header: "Code system",
        enableSorting: true,
      },
      {
        accessorKey: "patientReadStatus",
        header: "Read by patient",
        enableSorting: true,
      },
    ],
    []
  );

  const measurementsTotalPages = Math.max(
    1,
    Math.ceil(measurementsTotal / measurementsPageSize)
  );

  return (
    <PanelStack>
      <SectionCard>
        <SectionHeader>
          <SectionHeading>
            <SectionTitle>Conditions</SectionTitle>
            <SectionCaption>
              {currentCondition
                ? `Currently ${currentCondition.name}`
                : "No current condition"}
            </SectionCaption>
          </SectionHeading>
          <HeaderActions>
            <FilterButton
              variant="primary"
              onClick={() =>
                openModal(
                  <ChangeConditionModal
                    patient={patient}
                    currentCondition={currentCondition}
                    onDone={closeModal}
                  />,
                  { width: "560px", showCloseButton: true }
                )
              }
            >
              Change condition
            </FilterButton>
          </HeaderActions>
        </SectionHeader>

        {conditionsError && (
          <ErrorMessage>
            Failed to load this patient&apos;s conditions.
          </ErrorMessage>
        )}

        <TableWrapper refreshKeys={[[PATIENT_CONDITIONS_QUERY_KEY]]}>
          <TableSorted
            columns={conditionColumns}
            data={conditions}
            emptyStateText={
              conditionsLoading ? <TableLoadingState /> : "No conditions"
            }
          />
        </TableWrapper>
      </SectionCard>

      <SectionCard>
        <SectionHeader>
          <SectionHeading>
            <SectionTitle>Measurements</SectionTitle>
            <SectionCaption>
              {measurementsTotal} recorded — open a row for its values
            </SectionCaption>
          </SectionHeading>
        </SectionHeader>

        {measurementsError && (
          <ErrorMessage>
            Failed to load this patient&apos;s measurements.
          </ErrorMessage>
        )}

        <TableContainer>
          <TableWrapper refreshKeys={[["admin-patient-measurements"]]}>
            <TableSorted
              columns={measurementColumns}
              data={measurements}
              emptyStateText={
                measurementsFetching ? (
                  <TableLoadingState />
                ) : (
                  "No measurements recorded"
                )
              }
            />
          </TableWrapper>
          <TablePagination
            pagination={{
              currentPage: measurementsPage,
              pageSize: measurementsPageSize,
              totalPages: measurementsTotalPages,
              totalItems: measurementsTotal,
              hasNextPage: measurementsPage < measurementsTotalPages,
              hasPreviousPage: measurementsPage > 1,
            }}
            onPageChange={setMeasurementsPage}
            onPageSizeChange={(size) => {
              setMeasurementsPageSize(size);
              setMeasurementsPage(1);
            }}
            exportCsv={{
              filename: `patient-${patientId}-measurements`,
              columns: measurementColumns,
              // Server-paginated, so only the fetched page can be written.
              rows: measurements,
              currentPageOnly: true,
            }}
          />
        </TableContainer>
      </SectionCard>

      <SectionCard>
        <PatientBaselinesPanel patientId={patientId} />
      </SectionCard>

      <SectionCard>
        <SectionHeader>
          <SectionTitleGroup>
            <SectionTitle>Medications</SectionTitle>
            <PageInfoTooltip title="Medications">
              From the eMED integration
            </PageInfoTooltip>
          </SectionTitleGroup>
        </SectionHeader>

        {medicationsError && (
          <ErrorMessage>
            Failed to load this patient&apos;s medication history.
          </ErrorMessage>
        )}

        <TableContainer>
          <TableWrapper refreshKeys={[["admin-patient-medications"]]}>
            <TableSorted
              columns={medicationColumns}
              data={medicationsPagination.items}
              emptyStateText={
                medicationsLoading ? (
                  <TableLoadingState />
                ) : (
                  "No medication history"
                )
              }
            />
          </TableWrapper>
          <TablePagination
            pagination={medicationsPagination.pagination}
            onPageChange={medicationsPagination.setPage}
            onPageSizeChange={medicationsPagination.setPageSize}
            exportCsv={{
              filename: `patient-${patientId}-medications`,
              columns: medicationColumns,
              rows: medicationsPagination.allItems,
            }}
          />
        </TableContainer>
      </SectionCard>
    </PanelStack>
  );
}
